import { useState } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, Typography, Paper, Chip } from '@mui/material'
import { Add } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listSupplierCertifications, addSupplierCertification, type SupplierListItem } from '../../api/furnizori'
import { errorMessage } from '../../api/client'

const DAYS_WARNING = 60

function expiryStatus(expiresAt: string): { label: string; color: 'success' | 'warning' | 'error' } {
  const days = (new Date(expiresAt).getTime() - Date.now()) / 86_400_000
  if (days < 0) return { label: 'Expirată', color: 'error' }
  if (days <= DAYS_WARNING) return { label: `Expiră în ${Math.ceil(days)} zile`, color: 'warning' }
  return { label: 'Valabilă', color: 'success' }
}

/** FR-MDF-005 — certificări furnizor, cu semnalare vizuală a celor aproape de expirare. */
export default function SupplierCertificationsDialog({ supplier, onClose }: { supplier: SupplierListItem | null; onClose: () => void }) {
  const open = supplier !== null
  const qc = useQueryClient()
  const [type, setType] = useState('')
  const [issuedAt, setIssuedAt] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: items } = useQuery({
    queryKey: ['achizitii', 'furnizor-certificari', supplier?.id],
    queryFn: () => listSupplierCertifications(supplier!.id),
    enabled: open,
  })

  const mutation = useMutation({
    mutationFn: () => addSupplierCertification(supplier!.id, { type, issuedAt: issuedAt || null, expiresAt, notes: null }),
    onSuccess: () => {
      setType(''); setIssuedAt(''); setExpiresAt('')
      void qc.invalidateQueries({ queryKey: ['achizitii', 'furnizor-certificari', supplier?.id] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Certificări — {supplier?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <TextField size="small" label="Tip certificare" value={type} onChange={(e) => setType(e.target.value)} sx={{ flexGrow: 1 }} />
            <TextField size="small" type="date" label="Emisă la" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }} />
            <TextField size="small" type="date" label="Expiră la" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }} />
            <Button variant="outlined" startIcon={<Add />} disabled={!type || !expiresAt || mutation.isPending} onClick={() => mutation.mutate()}>
              Adaugă
            </Button>
          </Stack>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack spacing={1}>
            {items?.length === 0 && <Typography variant="body2" color="text.secondary">Nicio certificare înregistrată încă.</Typography>}
            {items?.map((c) => {
              const status = expiryStatus(c.expiresAt)
              return (
                <Paper key={c.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Stack sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.type}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.issuedAt ? `Emisă ${c.issuedAt} · ` : ''}Expiră {c.expiresAt}
                    </Typography>
                  </Stack>
                  <Chip label={status.label} size="small" color={status.color} variant="outlined" />
                </Paper>
              )
            })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
